import { z } from "zod";

/** Canonical payroll provider values the USER can pick. Drives form dropdowns. */
export const PAYROLL_SYSTEMS = ["ADP", "ADP WFN", "Paylocity", "SurePayroll", "Gusto"] as const;
export type PayrollSystem = (typeof PAYROLL_SYSTEMS)[number];

/** Pay schedules the USER can pick. Drives form dropdowns. */
export const PAYROLL_SCHEDULES = [
	"Weekly",
	"Bi-Weekly",
	"Semi-Monthly",
	"Monthly",
	"Quarterly",
	"Random",
	"Seasonal",
] as const;
export type PayrollSchedule = (typeof PAYROLL_SCHEDULES)[number];

/**
 * Storage-shape enums — everything in PAYROLL_SYSTEMS / PAYROLL_SCHEDULES plus
 * the "unknown" transient sentinel written by `syncQboCustomers` when a QBO
 * customer has no local match yet. Keep the user-pickable sets above narrow
 * so the form dropdowns don't expose "unknown" as a choice; this set is what
 * the Postgres CHECK constraints and read-path schemas accept.
 */
export const PAYROLL_SYSTEM_STORAGE_VALUES = [...PAYROLL_SYSTEMS, "unknown"] as const;
export const PAYROLL_SCHEDULE_STORAGE_VALUES = [...PAYROLL_SCHEDULES, "unknown"] as const;

/** Billing modes: per-pay (invoice per payroll run) or monthly (one invoice/month). */
export const BILLING_MODES = ["per_pay", "monthly"] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

/**
 * Wire shape for creating or updating a customer. All dollar amounts are
 * integer cents. The API router uses this schema for input validation and
 * the web form uses it (via zodResolver) as its single source of truth —
 * dollar text inputs are wrapped in Controllers that convert string<->cents
 * so the RHF form state matches this shape exactly.
 */
export const customerInputSchema = z.object({
	name: z.string().min(1).max(255),
	// Nullable — non-payroll (QBO-only) customers have no payroll system /
	// schedule. The form requires a value when editing a payrollCustomer row;
	// other customers stay null without a billing model.
	payrollSystem: z.enum(PAYROLL_SYSTEM_STORAGE_VALUES).nullable(),
	companyCode: z.string().nullable().default(null),
	schedule: z.enum(PAYROLL_SCHEDULE_STORAGE_VALUES).nullable(),
	billingMode: z.enum(BILLING_MODES).default("per_pay"),
	baseFee: z.number().int().nonnegative(),
	baseEeLimit: z.number().int().nonnegative(),
	perEeFee: z.number().int().nonnegative(),
	deliveryFee: z.number().int().nonnegative().default(0),
	timekeepingFeePerEe: z.number().int().nonnegative().default(0),
	stateFeeRate: z.number().int().nonnegative().default(0),
	taxFilingIncluded: z.boolean().default(false),
	workersCompFee: z.number().int().nonnegative().default(0),
	maintenanceFee: z.number().int().nonnegative().default(0),
	passThrough: z.boolean().default(false),
	shippingFee: z.number().int().nonnegative().default(0),
	payTransactionFee: z.number().int().nonnegative().default(0),
	childSupportFee: z.number().int().nonnegative().default(0),
	ssnVerificationFee: z.number().int().nonnegative().default(0),
	sameDayAchFee: z.number().int().nonnegative().default(0),
	yearEndBaseFee: z.number().int().nonnegative().default(0),
	yearEndPerFormFee: z.number().int().nonnegative().default(0),
	managePayrollSupportFee: z.number().int().nonnegative().default(0),
	hrBasicSupportFee: z.number().int().nonnegative().default(0),
	hrPlusSupportFee: z.number().int().nonnegative().default(0),
	taxReturnAmendmentFee: z.number().int().nonnegative().default(0),
	nameAliases: z.array(z.string()).default([]),
	notes: z.string().nullable().default(null),
	priceIncrease: z
		.object({
			effectiveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
			newBaseFee: z.number().int().nonnegative(),
			newPerEeFee: z.number().int().nonnegative().optional(),
		})
		.nullable()
		.default(null),
});

export type CustomerInput = z.infer<typeof customerInputSchema>;
